import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, Gift } from "lucide-react";

const PARTS = [
  {
    name: "Part I — The Foundation",
    chapters: [
      "The Truth About Sustainable Weight Loss",
      "Why Quick Fixes Fail",
      "The Role of Tea in a Healthy Routine",
      "Hunger, Habits, and Self-Trust",
      "Setting a Compassionate Starting Point",
    ],
  },
  {
    name: "Part II — Tea, Science & Rituals",
    chapters: [
      "What Tea Can and Cannot Do",
      "Hydration and Smarter Beverage Choices",
      "Green Tea, Herbal Tea, and Caffeine",
      "Ingredients Worth Knowing",
      "Safe Brewing and Mindful Drinking",
    ],
  },
  {
    name: "Part III — Eating for Satisfaction",
    chapters: [
      "Why Hunger Matters",
      "Building Balanced, Satisfying Meals",
      "Protein, Fiber, and Food Volume",
      "Emotional Eating with Curiosity",
      "Flexible Planning for Real Life",
    ],
  },
  {
    name: "Part IV — Habits That Support You",
    chapters: [
      "Sleep and Appetite",
      "Stress, Cravings, and Nervous System Care",
      "Gentle Movement Without the Gym",
      "Your Environment Shapes Your Choices",
      "Consistency Over Perfection",
    ],
  },
  {
    name: "Part V — Make It a Lifestyle",
    chapters: [
      "The 7-Day Gentle Reset",
      "The 14-Day Consistency Plan",
      "A 30-Day Sustainable Routine",
      "Handling Setbacks Without Giving Up",
      "Your Long-Term Wellness Blueprint",
    ],
  },
];

const UNLOCKS = [
  "25 in-depth chapters filled with practical, honest knowledge",
  "40+ tea & infusion recipes with step-by-step instructions",
  "Herb & ingredient guide — benefits and how to use them",
  "7-Day Reset, 14-Day Plan & 30-Day Routine to get started",
  "Tips to avoid the most common weight-loss mistakes",
  "Bonus: meal & lifestyle tips to support your journey",
];

export default function WhatsInside() {
  const [open, setOpen] = useState(0);

  return (
    <section id="inside" className="py-16 sm:py-24 lg:py-32 bg-[#F3EDE2]" data-testid="inside-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mb-14"
        >
          <p className="text-xs sm:text-sm font-bold tracking-[0.24em] uppercase text-[#D4A359] mb-4">
            Inside the ebook
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A241E] leading-tight" data-testid="inside-heading">
            25 chapters. 5 parts. One gentle method.
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-3"
            data-testid="chapters-accordion"
          >
            {PARTS.map((part, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={part.name}
                  className={`rounded-xl border overflow-hidden transition-colors duration-400 ${
                    isOpen ? "bg-white border-[#D4A359]/60 shadow-lg" : "bg-white/60 border-[#1C3A27]/10"
                  }`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between px-5 sm:px-6 py-4 text-left"
                    data-testid={`chapter-tab-${i}`}
                  >
                    <span className="font-display text-lg sm:text-xl font-semibold text-[#1A241E]">{part.name}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-[#1C3A27] transition-transform duration-400 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        {part.chapters.map((ch) => (
                          <li key={ch} className="flex items-start gap-3 px-5 sm:px-6 pb-3 text-sm text-[#5C6A60]">
                            <Check className="w-4 h-4 mt-0.5 text-[#D4A359] shrink-0" strokeWidth={3} />
                            {ch}
                          </li>
                        ))}
                        <div className="pb-3" />
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:sticky lg:top-24"
          >
            <div className="rounded-2xl overflow-hidden mb-8 shadow-2xl ring-1 ring-[#1C3A27]/10">
              <img
                src="https://images.pexels.com/photos/7596899/pexels-photo-7596899.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Woman enjoying a cup of herbal tea"
                className="w-full h-56 sm:h-64 object-cover"
              />
            </div>
            <p className="text-xs font-bold tracking-[0.24em] uppercase text-[#1C3A27] mb-5">
              You unlock immediately
            </p>
            <ul className="space-y-3.5" data-testid="unlocks-list">
              {UNLOCKS.map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="flex items-start gap-3 text-sm sm:text-base text-[#1A241E]"
                >
                  <span className="w-5 h-5 rounded-full bg-[#1C3A27] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#D4A359]" strokeWidth={3.5} />
                  </span>
                  {item}
                </motion.li>
              ))}
            </ul>
            <div className="mt-8 flex items-center gap-3 rounded-xl bg-[#1C3A27] text-[#FAF7F2] px-5 py-4" data-testid="bonus-callout">
              <Gift className="w-6 h-6 text-[#D4A359] shrink-0" />
              <p className="text-sm">
                <span className="font-bold text-[#D4A359]">Bonus:</span> the full Recipe Collection —
                from Berry Hibiscus Sparkler to Gentle Spiced Chai, plus how to build your own signature blend.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
